import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Sparkles, Plus, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { v: "incoming", l: "Incoming" },
  { v: "in_process", l: "In-Process" },
  { v: "final", l: "Final" },
];

export default function QualityPage() {
  const [list, setList] = useState([]);
  const [items, setItems] = useState([]);
  const [wos, setWos] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [globalInsight, setGlobalInsight] = useState("");
  const [busyAI, setBusyAI] = useState(false);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    item_id: "", work_order_id: "", type: "in_process", status: "pending", inspector: "",
    checkpoints: [{ name: "Dimension", expected: "", actual: "", passed: true, notes: "" }],
    defects: [],
  });

  const load = async () => {
    const [q, i, w] = await Promise.all([api.get("/quality"), api.get("/items"), api.get("/work-orders")]);
    setList(q.data);
    setItems(i.data);
    setWos(w.data);
    if (!form.item_id && i.data.length) setForm((f) => ({ ...f, item_id: i.data[0].id }));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const itemMap = Object.fromEntries(items.map((i) => [i.id, i]));
  const woMap = Object.fromEntries(wos.map((w) => [w.id, w]));

  const create = async () => {
    if (busy) return;
    if (!form.item_id) return toast.error("Pick an item");
    setBusy(true);
    try {
      const payload = { ...form, work_order_id: form.work_order_id || null };
      await api.post("/quality", payload);
      toast.success("Inspection logged");
      setOpen(false);
      setForm({
        item_id: items[0]?.id || "", work_order_id: "", type: "in_process", status: "pending", inspector: "",
        checkpoints: [{ name: "Dimension", expected: "", actual: "", passed: true, notes: "" }],
        defects: [],
      });
      load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete inspection?")) return;
    await api.delete(`/quality/${id}`);
    if (selected?.id === id) setSelected(null);
    load();
  };

  const generateAI = async (qi) => {
    setBusyAI(true);
    try {
      const { data } = await api.post(`/quality/${qi.id}/ai-summary`);
      toast.success("AI summary ready");
      setSelected({ ...qi, ai_summary: data.ai_summary });
      load();
    } catch {
      toast.error("AI summary failed");
    } finally {
      setBusyAI(false);
    }
  };

  const runGlobalInsight = async () => {
    setBusyAI(true);
    try {
      const { data } = await api.post("/quality/insights/global");
      setGlobalInsight(data.insight);
    } catch { toast.error("Insight failed"); }
    finally { setBusyAI(false); }
  };

  const addCp = () => setForm((f) => ({ ...f, checkpoints: [...f.checkpoints, { name: "", expected: "", actual: "", passed: true, notes: "" }] }));
  const setCp = (i, p) => setForm((f) => ({ ...f, checkpoints: f.checkpoints.map((c, idx) => idx === i ? { ...c, ...p } : c) }));
  const rmCp = (i) => setForm((f) => ({ ...f, checkpoints: f.checkpoints.filter((_, idx) => idx !== i) }));

  const addDf = () => setForm((f) => ({ ...f, defects: [...f.defects, { code: "", description: "", severity: "low", qty: 1 }] }));
  const setDf = (i, p) => setForm((f) => ({ ...f, defects: f.defects.map((c, idx) => idx === i ? { ...c, ...p } : c) }));
  const rmDf = (i) => setForm((f) => ({ ...f, defects: f.defects.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-6" data-testid="quality-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">QA</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Quality Inspections
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runGlobalInsight} disabled={busyAI} data-testid="global-insight-btn">
            <Sparkles className="w-4 h-4 mr-1.5 text-[var(--ai-accent)]" /> Plant Insight
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-qc-btn"><Plus className="w-4 h-4 mr-1.5" />New Inspection</Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
              <SheetHeader><SheetTitle>New Inspection</SheetTitle></SheetHeader>
              <div className="space-y-4 mt-6">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Item</Label>
                    <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                      <SelectTrigger data-testid="qc-item-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.code} — {it.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Work Order (optional)</Label>
                    <Select value={form.work_order_id || "none"} onValueChange={(v) => setForm({ ...form, work_order_id: v === "none" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {wos.map((w) => <SelectItem key={w.id} value={w.id}>{w.code}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="passed">Passed</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Inspector</Label>
                    <Input value={form.inspector} onChange={(e) => setForm({ ...form, inspector: e.target.value })} />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-slate-700">Checkpoints</div>
                    <Button size="sm" variant="outline" onClick={addCp}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
                  </div>
                  <div className="space-y-2">
                    {form.checkpoints.map((c, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-3"><Label className="text-[10px] uppercase">Name</Label><Input value={c.name} onChange={(e) => setCp(idx, { name: e.target.value })} /></div>
                        <div className="col-span-3"><Label className="text-[10px] uppercase">Expected</Label><Input value={c.expected} onChange={(e) => setCp(idx, { expected: e.target.value })} /></div>
                        <div className="col-span-3"><Label className="text-[10px] uppercase">Actual</Label><Input value={c.actual} onChange={(e) => setCp(idx, { actual: e.target.value })} /></div>
                        <div className="col-span-2">
                          <Label className="text-[10px] uppercase">Result</Label>
                          <Select value={c.passed ? "yes" : "no"} onValueChange={(v) => setCp(idx, { passed: v === "yes" })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="yes">Pass</SelectItem><SelectItem value="no">Fail</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-1"><Button variant="ghost" size="icon" onClick={() => rmCp(idx)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-slate-700">Defects</div>
                    <Button size="sm" variant="outline" onClick={addDf}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
                  </div>
                  <div className="space-y-2">
                    {form.defects.length === 0 && <div className="text-xs text-slate-500">No defects logged.</div>}
                    {form.defects.map((d, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-2"><Label className="text-[10px] uppercase">Code</Label><Input value={d.code} onChange={(e) => setDf(idx, { code: e.target.value })} /></div>
                        <div className="col-span-5"><Label className="text-[10px] uppercase">Description</Label><Input value={d.description} onChange={(e) => setDf(idx, { description: e.target.value })} /></div>
                        <div className="col-span-2">
                          <Label className="text-[10px] uppercase">Severity</Label>
                          <Select value={d.severity} onValueChange={(v) => setDf(idx, { severity: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="critical">Critical</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2"><Label className="text-[10px] uppercase">Qty</Label><Input type="number" value={d.qty} onChange={(e) => setDf(idx, { qty: parseInt(e.target.value || 1, 10) })} /></div>
                        <div className="col-span-1"><Button variant="ghost" size="icon" onClick={() => rmDf(idx)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <SheetFooter className="mt-6">
                <Button onClick={create} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="save-qc-btn">Save Inspection</Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {globalInsight && (
        <div className="ai-card rounded-lg p-6" data-testid="global-insight-card">
          <div className="flex items-center gap-2 mb-2 text-[var(--ai-accent)]">
            <Sparkles className="w-4 h-4" />
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold">Plant Quality Insight</div>
          </div>
          <div className="text-sm text-slate-700 leading-relaxed">{globalInsight}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Inspections</div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto scroll-thin">
            {list.map((q) => (
              <button
                key={q.id}
                onClick={() => setSelected(q)}
                data-testid={`qc-row-${q.code}`}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${selected?.id === q.id ? "bg-blue-50/60" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mono">{q.code}</div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full
                    ${q.status === "passed" ? "bg-emerald-50 text-emerald-700" : q.status === "failed" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                    {q.status}
                  </span>
                </div>
                <div className="mt-1 text-sm font-medium text-slate-800">{itemMap[q.item_id]?.code} — {itemMap[q.item_id]?.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{(q.type || "unspecified").replace("_", " ")} · {q.defects?.length || 0} defects</div>
              </button>
            ))}
            {list.length === 0 && <div className="px-4 py-10 text-center text-sm text-slate-500">No inspections yet.</div>}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[60vh]" data-testid="qc-detail">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">Select an inspection to view details.</div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{selected.code}</div>
                  <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>
                    {itemMap[selected.item_id]?.code} — {itemMap[selected.item_id]?.name}
                  </h2>
                  <div className="text-xs text-slate-500 mt-1">
                    {(selected.type || "unspecified").replace("_", " ")} · {selected.inspector || "Inspector"}
                    {selected.work_order_id ? ` · ${woMap[selected.work_order_id]?.code}` : ""}
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(selected.id)} data-testid="qc-delete-btn">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>

              <div className="ai-card rounded-lg p-5" data-testid="qc-ai-summary">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-[var(--ai-accent)]">
                    <Sparkles className="w-4 h-4" />
                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold">AI Defect Summary</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => generateAI(selected)} disabled={busyAI} data-testid="generate-ai-btn">
                    <Wand2 className="w-3.5 h-3.5 mr-1" />{busyAI ? "Generating..." : "Generate"}
                  </Button>
                </div>
                <div className="text-sm text-slate-700 leading-relaxed min-h-[3rem]">
                  {selected.ai_summary || "Click Generate to summarize defects and suggest corrective actions."}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Checkpoints</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
                      <th className="py-2">Name</th><th className="py-2">Expected</th><th className="py-2">Actual</th><th className="py-2">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.checkpoints.map((c, idx) => (
                      <tr key={idx} className="border-t border-slate-100">
                        <td className="py-2">{c.name}</td>
                        <td className="py-2 mono text-slate-600">{c.expected}</td>
                        <td className="py-2 mono">{c.actual}</td>
                        <td className="py-2">
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${c.passed ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                            {c.passed ? "Pass" : "Fail"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selected.defects?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Defects</div>
                  <div className="space-y-2">
                    {selected.defects.map((d, idx) => (
                      <div key={idx} className="border border-slate-200 rounded-md p-3 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold text-slate-800">{d.code} — {d.description}</div>
                          <div className="text-xs text-slate-500 mt-0.5">Qty {d.qty}</div>
                        </div>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full
                          ${d.severity === "critical" ? "bg-red-50 text-red-700" : d.severity === "high" ? "bg-amber-50 text-amber-700"
                            : d.severity === "medium" ? "bg-yellow-50 text-yellow-700" : "bg-slate-100 text-slate-600"}`}>
                          {d.severity}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
